// Developer tokens — thin adapters over valoi_engine's authenticated
// /api/v1/tokens endpoints. Identity comes from the Bearer JWT via apiClient
// (same as every other real call), so none of these take a userId.
//
// These endpoints are browser-session only on purpose: the engine refuses token
// management when the caller authenticated *with* a developer token, so a leaked
// token cannot mint replacements to survive being revoked. That means this module
// only ever works from a signed-in web session — never from the CLI.
//
// The raw secret exists exactly once, in the createToken() response. Nothing can
// recover it afterwards (the engine stores only a sha256), so the caller MUST
// surface it immediately.

import { deleteJson, getJson, postJson } from '@/shared/api/apiClient';
import type { CreatedDeveloperToken, DeveloperToken } from '@/shared/types';

const TOKENS_BASE = '/api/v1/tokens';

type DeveloperTokenApiResponse = {
  id: string;
  name: string;
  token_prefix: string;
  created_at: string;
  last_used_at: string | null;
  expires_at: string | null;
};

type CreatedDeveloperTokenApiResponse = DeveloperTokenApiResponse & {
  token: string;
};

function mapToken(body: DeveloperTokenApiResponse): DeveloperToken {
  return {
    id: body.id,
    name: body.name,
    tokenPrefix: body.token_prefix,
    createdAt: body.created_at,
    lastUsedAt: body.last_used_at,
    expiresAt: body.expires_at,
  };
}

export async function listTokens(): Promise<DeveloperToken[]> {
  const body = await getJson<DeveloperTokenApiResponse[]>(TOKENS_BASE);
  return (body ?? []).map(mapToken);
}

export async function createToken(
  name: string,
  expiresInDays?: number | null,
): Promise<CreatedDeveloperToken> {
  const body = await postJson<CreatedDeveloperTokenApiResponse>(TOKENS_BASE, {
    name,
    // Omitted entirely rather than sent as null when unset, so the engine applies
    // its own "never expires" default.
    ...(expiresInDays ? { expires_in_days: expiresInDays } : {}),
  });
  return { ...mapToken(body), token: body.token };
}

export async function revokeToken(id: string): Promise<void> {
  // The engine returns 204; handleResponse maps that to undefined.
  await deleteJson<void>(`${TOKENS_BASE}/${encodeURIComponent(id)}`);
}
